/**
 * The weekly plant digest (plant-analytics B-8 and B-8b, brief 4.5 "Weekly digest" and 4.7 "Digest and study pages"):
 * typed wrappers over the digest read routes and their hooks. Backend `internal/twin/digest_routes.go`; the JSON
 * there is the contract.
 *
 *   GET /v1/twin/digests?limit=&offset=      the org's digests, newest plant week first, the latest revision of each
 *   GET /v1/twin/digests/{digest_id}          one snapshot: metadata, the revision chain, the document and its claims
 *   GET /v1/twin/digests/{digest_id}/html     the stored page; ?inline=1 is the print route
 *
 * WHO SEES WHAT is the server's: a platform admin sees every snapshot of the active org, internal (shadow) ones
 * included; anyone else only the customer-visible ones, and an internal or withdrawn snapshot is 404 to them. The web
 * never re-derives that rule: an "Internal (shadow)" badge shows only because the data says `visibility: "internal"`.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { statusOf } from "./error";

/** The plant clock a digest is cut on (reportjobs.Stamp). */
export interface DigestPlantClock {
  /** The plant's zone; UTC when it has none. */
  tz: string;
  /** The production day start, HH:MM. */
  day_start: string;
  /** ISO weekday the plant week starts on: 1 is Monday, 7 is Sunday. */
  week_start: number;
  /**
   * The plant's shifts in day order, each starting at an HH:MM local to `tz` and running to the next one's start (the
   * last over midnight). Absent when the plant has none, and on a snapshot recorded before 2026-09-26.
   */
  shifts?: { start: string; label: string }[];
}

/** One digest snapshot in the list (twin.DigestSummary). */
export interface DigestSummary {
  id: string;
  /** The plant week's first day, YYYY-MM-DD. */
  period_from: string;
  /** The plant day after its last (half-open), YYYY-MM-DD. */
  period_to: string;
  /** The week's revision: from 2 on, late data changed the week after its first revision. */
  revision: number;
  /**
   * An earlier revision of the week is one the caller may see (for a member, an earlier customer revision, the rule the
   * email follows). The page and the list say "Revised" only then: a shadow week's first customer revision is not
   * revised to the org's members (#930 review, LOW).
   */
  revised: boolean;
  status: "published" | "superseded" | "withdrawn";
  visibility: "customer" | "internal";
  title: string;
  published_at: string;
  report_run_id: string | null;
  plant_clock: DigestPlantClock;
}

/** GET /v1/twin/digests (httpx.List of DigestSummary). `total` is the length of this page. */
export interface DigestList {
  items: DigestSummary[];
  total: number;
  limit: number;
  offset: number;
}

/** One evidence record a claim cites (twin.DigestClaimEvidence). */
export interface DigestClaimEvidence {
  id: string;
  method: string;
  method_version: string;
  grain: string;
  /** Half-open plant dates [from, to); null when the record has none. */
  window: { from: string; to: string } | null;
  /** The rows the record was computed from; null when it has none. */
  n: number | null;
}

/** One claim of the document, for the claims side panel (twin.DigestClaim). */
export interface DigestClaim {
  id: string;
  type: "fact" | "inference" | "textbook";
  relation: string;
  grain: string;
  confidence: string;
  /** The caveat lexicon's keys ("association-not-cause"). */
  caveats: string[];
  /** The statement as plain text exactly as the page renders it (no markup). */
  statement_text: string;
  evidence: DigestClaimEvidence[];
  /** The claim's action as plain text, as the page renders it; absent for a claim with no action. */
  action_text?: string;
  /** A published study's action's recommendation key (L1); absent on a digest's claims. */
  recommendation_key?: string;
}

/** GET /v1/twin/digests/{digest_id} (twin.DigestDetail). */
export interface DigestDetail extends DigestSummary {
  supersedes_id: string | null;
  /** The next revision the caller may see, if any. */
  superseded_by_id: string | null;
  engine_version: string;
  contract_version: string;
  /** sha256 of the document as the worker serialized it, hex. */
  content_hash: string;
  watermark: Record<string, unknown>;
  registry: Record<string, unknown>;
  /** The reportdoc/v1 document. */
  document: Record<string, unknown>;
  /**
   * Empty when the stored document holds none, or does not decode; absent from an API older than B-8b (or a mock),
   * which the page reads as none (#920 review L6).
   */
  claims?: DigestClaim[];
}

/** The list reads at most this many digests at once (the server's cap). */
export const DIGESTS_PAGE_LIMIT = 200;

const d = (id: string) => `/v1/twin/digests/${encodeURIComponent(id)}`;

export const digestsApi = {
  list: () => api.get<DigestList>(`/v1/twin/digests?limit=${DIGESTS_PAGE_LIMIT}`),
  /** Whether the caller has any digest to read: one asked for, for the nav. */
  any: () => api.get<DigestList>("/v1/twin/digests?limit=1"),
  get: (id: string) => api.get<DigestDetail>(d(id)),
};

/**
 * The digest's page as HTML text, through the app's authenticated client (the bearer token and the active org ride
 * along), from the print route (`?inline=1`). The page is self-contained and inert (no script, no URL, its own CSP
 * meta); the detail route shows it in a sandboxed iframe's srcDoc and never as the app's own markup.
 */
export async function fetchDigestPage(id: string): Promise<string> {
  const blob = await api.getBlob(`${d(id)}/html?inline=1`);
  return blob.text();
}

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** True when the digest routes can be asked at all: a live API, a signed-in user, an active org. */
export function useDigestsLive(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

export const DIGESTS_KEY = ["twin-digests"] as const;
export const DIGESTS_NAV_KEY = ["twin-digests-nav"] as const;
export const DIGEST_KEY = ["twin-digest"] as const;
export const DIGEST_PAGE_KEY = ["twin-digest-page"] as const;

/** A 403 (a vendor overlay, or no data.read) and a 404 (not the caller's to see) are answers, never retried. */
const retryUnlessRefused = (count: number, err: unknown) => {
  const s = statusOf(err);
  return s !== 403 && s !== 404 && count < 2;
};

/** The org's digests as the caller may see them, newest week first. `enabled` lets the nav ask only when it shows. */
export function useDigestsQuery(enabled = true) {
  const live = useDigestsLive();
  return useQuery({
    queryKey: DIGESTS_KEY,
    queryFn: digestsApi.list,
    enabled: live && enabled,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * Whether the caller has any digest, asked for with one, for the nav. `enabled` is the nav item's own gate (the twin
 * pack): nothing is asked for an item the nav does not show.
 */
export function useDigestsNavQuery(enabled: boolean) {
  const live = useDigestsLive();
  return useQuery({
    queryKey: DIGESTS_NAV_KEY,
    queryFn: digestsApi.any,
    enabled: live && enabled,
    staleTime: 5 * 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * The Reports hub's "Past digests" tab, pure: shown once the list holds a digest the viewer may read (a platform
 * admin's holds the shadow weeks too). The digest is retired (D5, 2026-10-06), so a plant that never had one never
 * shows the tab. It fails closed: loading, a refusal and a failure all hide it.
 */
export function digestNavVisible(page: Pick<DigestList, "items"> | undefined): boolean {
  return (page?.items.length ?? 0) > 0;
}

/** One digest with its document and claims. */
export function useDigestQuery(id: string | undefined) {
  const live = useDigestsLive();
  return useQuery({
    queryKey: [...DIGEST_KEY, id ?? ""],
    queryFn: () => digestsApi.get(id as string),
    enabled: live && !!id,
    staleTime: 5 * 60_000,
    retry: retryUnlessRefused,
  });
}

/** The digest's stored page as HTML text, for the sandboxed iframe. A stored page never changes. */
export function useDigestPageQuery(id: string | undefined) {
  const live = useDigestsLive();
  return useQuery({
    queryKey: [...DIGEST_PAGE_KEY, id ?? ""],
    queryFn: () => fetchDigestPage(id as string),
    enabled: live && !!id,
    staleTime: Infinity,
    retry: retryUnlessRefused,
  });
}
